import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  DatePicker,
  Space,
  Row,
  Col,
  message,
  Statistic,
  Progress,
  Tag,
  Radio,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

// 渠道来源数据接口
interface ChannelSourceData {
  id: number;
  channelName: string; // 渠道名称
  promotionSource: string; // 推广途径（脸书、线下等）
  registerCount: number; // 注册用户数
  percentage: number; // 默认注册占比（保留字段，实际按选中指标实时计算）
  conversionRate: number; // 转化率（%）
  activeUserCount: number; // 活跃用户数
  betUserCount: number; // 投注人数
  betAmount: number; // 投注金额
  depositUserCount: number; // 充值用户数
  depositRate: number; // 充值转化率（%）
  totalDepositAmount: number; // 总充值金额
  avgDepositAmount: number; // 平均充值金额
}

type PieMetricType = 'register' | 'betUser' | 'betAmount' | 'depositUser' | 'depositAmount';

const pieMetricConfig: Record<
  PieMetricType,
  {
    label: string;
    unit: string;
    getValue: (item: ChannelSourceData) => number;
  }
> = {
  register: {
    label: '注册人数',
    unit: '人',
    getValue: (item) => item.registerCount,
  },
  betUser: {
    label: '投注人数',
    unit: '人',
    getValue: (item) => item.betUserCount,
  },
  betAmount: {
    label: '投注金额',
    unit: '元',
    getValue: (item) => item.betAmount,
  },
  depositUser: {
    label: '充值人数',
    unit: '人',
    getValue: (item) => item.depositUserCount,
  },
  depositAmount: {
    label: '充值金额',
    unit: '元',
    getValue: (item) => item.totalDepositAmount,
  },
};

// 推广途径列表
const promotionSources = ['脸书', '线下', 'Google广告', 'TikTok', 'Instagram', 'YouTube', '合作伙伴', '其他'];

// 渠道列表
const channelNames = [
  '官网直营',
  '代理商A',
  '代理商B',
  '代理商C',
  '社交媒体',
  '搜索引擎',
  '合作伙伴',
  '线下推广',
  '口碑推荐',
  '其他渠道',
];

// 生成随机数据
const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const randomFloat = (min: number, max: number) => Math.random() * (max - min) + min;

// 生成渠道来源数据
const generateChannelSourceData = (): ChannelSourceData[] => {
  const data: ChannelSourceData[] = [];
  let totalRegister = 0;

  // 先生成数据
  channelNames.forEach((channelName, index) => {
    const registerCount = randomInt(100, 5000);
    totalRegister += registerCount;
    
    const activeUserCount = Math.floor(registerCount * randomFloat(0.3, 0.8));
    const betUserCount = Math.floor(activeUserCount * randomFloat(0.6, 0.95));
    const betAmount = randomFloat(50000, 8000000);

    data.push({
      id: index + 1,
      channelName,
      promotionSource: promotionSources[Math.floor(Math.random() * promotionSources.length)],
      registerCount,
      percentage: 0, // 稍后计算
      conversionRate: randomFloat(5, 35),
      activeUserCount,
      betUserCount,
      betAmount,
      depositUserCount: Math.floor(registerCount * randomFloat(0.1, 0.5)),
      depositRate: randomFloat(8, 45),
      totalDepositAmount: randomFloat(100000, 10000000),
      avgDepositAmount: randomFloat(500, 5000),
    });
  });

  // 计算占比
  data.forEach(item => {
    item.percentage = (item.registerCount / totalRegister) * 100;
  });

  return data.sort((a, b) => b.registerCount - a.registerCount);
};

// 格式化数字
const formatNumber = (num: number) => {
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// 饼图颜色
const pieColors = [
  '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
  '#13c2c2', '#eb2f96', '#fa8c16', '#2f54eb', '#a0d911',
];

const ChannelSourceAnalysis: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<ChannelSourceData[]>(generateChannelSourceData());
  const [pieMetric, setPieMetric] = useState<PieMetricType>('register');

  // 当前饼图指标总值
  const totalPieMetricValue = dataSource.reduce(
    (sum, item) => sum + pieMetricConfig[pieMetric].getValue(item),
    0,
  );

  const getPiePercentage = (item: ChannelSourceData) => {
    const value = pieMetricConfig[pieMetric].getValue(item);
    if (!totalPieMetricValue) return 0;
    return (value / totalPieMetricValue) * 100;
  };

  // 计算总计
  const totalData = dataSource.reduce((acc, item) => ({
    registerCount: acc.registerCount + item.registerCount,
    activeUserCount: acc.activeUserCount + item.activeUserCount,
    betUserCount: acc.betUserCount + item.betUserCount,
    betAmount: acc.betAmount + item.betAmount,
    depositUserCount: acc.depositUserCount + item.depositUserCount,
    totalDepositAmount: acc.totalDepositAmount + item.totalDepositAmount,
  }), {
    registerCount: 0,
    activeUserCount: 0,
    betUserCount: 0,
    betAmount: 0,
    depositUserCount: 0,
    totalDepositAmount: 0,
  });

  const avgConversionRate = dataSource.length > 0
    ? dataSource.reduce((sum, item) => sum + item.conversionRate, 0) / dataSource.length
    : 0;
  const avgDepositRate = dataSource.length > 0
    ? dataSource.reduce((sum, item) => sum + item.depositRate, 0) / dataSource.length
    : 0;

  // 处理搜索
  const handleSearch = () => {
    setLoading(true);
    setTimeout(() => {
      setDataSource(generateChannelSourceData());
      setLoading(false);
      message.success('查询成功');
    }, 500);
  };

  // 处理重置
  const handleReset = () => {
    setDateRange([dayjs().subtract(7, 'day'), dayjs()]);
    setDataSource(generateChannelSourceData());
    message.info('已重置');
  };

  // 处理导出Excel
  const handleExportExcel = () => {
    message.success('Excel导出功能开发中...');
  };

  // 表格列定义
  const columns = [
    {
      title: '渠道名称',
      dataIndex: 'channelName',
      key: 'channelName',
      width: 150,
      fixed: 'left' as const,
    },
    {
      title: '推广途径',
      dataIndex: 'promotionSource',
      key: 'promotionSource',
      width: 120,
    },
    {
      title: '注册用户数',
      dataIndex: 'registerCount',
      key: 'registerCount',
      width: 120,
      align: 'right' as const,
      sorter: (a: ChannelSourceData, b: ChannelSourceData) => a.registerCount - b.registerCount,
      render: (num: number) => num.toLocaleString(),
    },
    {
      title: '占比',
      dataIndex: 'percentage',
      key: 'percentage',
      width: 150,
      align: 'right' as const,
      sorter: (a: ChannelSourceData, b: ChannelSourceData) => getPiePercentage(a) - getPiePercentage(b),
      render: (_: number, record: ChannelSourceData, index: number) => {
        const percentage = getPiePercentage(record);
        return (
          <div>
            <Progress
              percent={percentage}
              size="small"
              strokeColor={pieColors[index % pieColors.length]}
              format={() => `${percentage.toFixed(2)}%`}
            />
          </div>
        );
      },
    },
    {
      title: '转化率',
      dataIndex: 'conversionRate',
      key: 'conversionRate',
      width: 120,
      align: 'right' as const,
      sorter: (a: ChannelSourceData, b: ChannelSourceData) => a.conversionRate - b.conversionRate,
      render: (rate: number) => (
        <Tag color={rate >= 20 ? 'success' : rate >= 10 ? 'warning' : 'default'}>
          {rate.toFixed(2)}%
        </Tag>
      ),
    },
    {
      title: '活跃用户数',
      dataIndex: 'activeUserCount',
      key: 'activeUserCount',
      width: 120,
      align: 'right' as const,
      sorter: (a: ChannelSourceData, b: ChannelSourceData) => a.activeUserCount - b.activeUserCount,
      render: (num: number) => num.toLocaleString(),
    },
    {
      title: '充值用户数',
      dataIndex: 'depositUserCount',
      key: 'depositUserCount',
      width: 120,
      align: 'right' as const,
      sorter: (a: ChannelSourceData, b: ChannelSourceData) => a.depositUserCount - b.depositUserCount,
      render: (num: number) => num.toLocaleString(),
    },
    {
      title: '充值转化率',
      dataIndex: 'depositRate',
      key: 'depositRate',
      width: 120,
      align: 'right' as const,
      sorter: (a: ChannelSourceData, b: ChannelSourceData) => a.depositRate - b.depositRate,
      render: (rate: number) => (
        <Tag color={rate >= 30 ? 'success' : rate >= 15 ? 'warning' : 'default'}>
          {rate.toFixed(2)}%
        </Tag>
      ),
    },
    {
      title: '总充值金额',
      dataIndex: 'totalDepositAmount',
      key: 'totalDepositAmount',
      width: 150,
      align: 'right' as const,
      sorter: (a: ChannelSourceData, b: ChannelSourceData) => a.totalDepositAmount - b.totalDepositAmount,
      render: (num: number) => formatNumber(num),
    },
    {
      title: '平均充值金额',
      dataIndex: 'avgDepositAmount',
      key: 'avgDepositAmount',
      width: 150,
      align: 'right' as const,
      sorter: (a: ChannelSourceData, b: ChannelSourceData) => a.avgDepositAmount - b.avgDepositAmount,
      render: (num: number) => formatNumber(num),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>
            渠道来源分析报表 {dateRange[0].format('YYYY-MM-DD')} - {dateRange[1].format('YYYY-MM-DD')}
          </h2>
        </div>

        {/* 查询条件 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Space>
              <span>时间范围：</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY-MM-DD"
              />
            </Space>
          </Col>
          <Col span={16}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                查询
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExportExcel}>
                导出Excel
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总注册用户数"
                value={totalData.registerCount}
                valueStyle={{ color: '#1890ff' }}
                suffix="人"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总活跃用户数"
                value={totalData.activeUserCount}
                valueStyle={{ color: '#52c41a' }}
                suffix="人"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均转化率"
                value={avgConversionRate}
                precision={2}
                valueStyle={{ color: '#faad14' }}
                suffix="%"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均充值转化率"
                value={avgDepositRate}
                precision={2}
                valueStyle={{ color: '#f5222d' }}
                suffix="%"
              />
            </Card>
          </Col>
        </Row>

        {/* 饼图展示 */}
        <Card
          title={
            <Space>
              <PieChartOutlined />
              <span>
                渠道占比分布（按
                {pieMetricConfig[pieMetric].label}
                ）
              </span>
            </Space>
          }
          extra={(
            <Radio.Group
              size="small"
              value={pieMetric}
              onChange={(e) => setPieMetric(e.target.value)}
            >
              <Radio.Button value="register">注册人数</Radio.Button>
              <Radio.Button value="betUser">投注人数</Radio.Button>
              <Radio.Button value="betAmount">投注金额</Radio.Button>
              <Radio.Button value="depositUser">充值人数</Radio.Button>
              <Radio.Button value="depositAmount">充值金额</Radio.Button>
            </Radio.Group>
          )}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ position: 'relative', width: 300, height: 300 }}>
                  {/* 使用CSS创建简单的饼图 */}
                  <svg width="300" height="300" viewBox="0 0 200 200" style={{ transform: 'rotate(-90deg)' }}>
                    {dataSource.map((item, index) => {
                      const startAngle = dataSource
                        .slice(0, index)
                        .reduce((sum, d) => sum + getPiePercentage(d) * 3.6, 0);
                      const angle = getPiePercentage(item) * 3.6;
                      const largeArcFlag = angle > 180 ? 1 : 0;
                      const x1 = 100 + 100 * Math.cos((startAngle * Math.PI) / 180);
                      const y1 = 100 + 100 * Math.sin((startAngle * Math.PI) / 180);
                      const x2 = 100 + 100 * Math.cos(((startAngle + angle) * Math.PI) / 180);
                      const y2 = 100 + 100 * Math.sin(((startAngle + angle) * Math.PI) / 180);
                      
                      return (
                        <path
                          key={item.id}
                          d={`M 100 100 L ${x1} ${y1} A 100 100 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                          fill={pieColors[index % pieColors.length]}
                          stroke="#fff"
                          strokeWidth="2"
                        />
                      );
                    })}
                  </svg>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ padding: '20px 0' }}>
                {dataSource.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 16,
                      padding: '8px 0',
                    }}
                  >
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: pieColors[index % pieColors.length],
                        marginRight: 12,
                        borderRadius: 2,
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold' }}>{item.channelName}</span>
                        <span style={{ color: '#666', marginLeft: 16 }}>
                          {item.percentage.toFixed(2)}% ({item.registerCount.toLocaleString()}人)
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                        推广途径：{item.promotionSource}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Card>

        {/* 数据表格 */}
        <Table
          rowKey="id"
          dataSource={dataSource}
          columns={columns}
          loading={loading}
          pagination={{
            total: dataSource.length,
            pageSize: 20,
            showTotal: (total) => `共 ${total} 条`,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: 1500 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                <Table.Summary.Cell index={0} colSpan={2}>
                  总计
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  {totalData.registerCount.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  100.00%
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  {avgConversionRate.toFixed(2)}%
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  {totalData.activeUserCount.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  {totalData.depositUserCount.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  {avgDepositRate.toFixed(2)}%
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">
                  {formatNumber(totalData.totalDepositAmount)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="right">
                  {formatNumber(totalData.totalDepositAmount / (totalData.depositUserCount || 1))}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
    </div>
  );
};

export default ChannelSourceAnalysis;

